import openai from './openaiClient';

/**
 * Generates a chat completion response based on user input using GPT-5.
 * @param {string} userMessage - The user's input message.
 * @param {string} systemPrompt - Optional system prompt for context.
 * @returns {Promise<string>} The assistant's response.
 */
export async function getBasicChatCompletion(userMessage, systemPrompt = 'You are a helpful assistant specialized in product matching and price analysis.') {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini', // Using available model
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage },
      ],
      max_tokens: 2000,
      temperature: 0.7,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error in basic chat completion:', error);
    throw error;
  }
}

/**
 * Streams a chat completion response chunk by chunk.
 * @param {string} userMessage - The user's input message.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {string} systemPrompt - Optional system prompt for context.
 */
export async function getStreamingChatCompletion(userMessage, onChunk, systemPrompt = 'You are a helpful assistant specialized in product matching and price analysis.') {
  try {
    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage },
      ],
      stream: true,
      max_tokens: 2000,
      temperature: 0.7,
    });

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        onChunk(content);
      }
    }
  } catch (error) {
    console.error('Error in streaming chat completion:', error);
    throw error;
  }
}

/**
 * Analyzes product data and provides matching suggestions.
 * @param {string} productData - Product data to analyze.
 * @returns {Promise<Object>} Structured analysis with matching suggestions.
 */
export async function analyzeProductMatching(productData) {
  try {
    const systemPrompt = `You are an expert product matching analyst. Analyze the provided product data and suggest potential matches, pricing insights, and categorization improvements. Return your analysis in JSON format with the following structure:
    {
      "analysis": "detailed analysis of the product",
      "potential_matches": ["list of potential matching products"],
      "pricing_insights": "pricing analysis and recommendations",
      "confidence_score": "number between 0 and 1",
      "recommendations": ["list of actionable recommendations"]
    }`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Analyze this product data: ${productData}` },
      ],
      response_format: { type: 'json_object' },
      max_tokens: 1500,
      temperature: 0.3,
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error in product matching analysis:', error);
    throw error;
  }
}

/**
 * Generates supplier data insights and recommendations.
 * @param {Array} supplierData - Array of supplier data objects.
 * @returns {Promise<Object>} Structured supplier analysis.
 */
export async function analyzeSupplierData(supplierData) {
  try {
    const systemPrompt = `You are a supply chain analyst. Analyze the provided supplier data and provide insights about pricing trends, supplier reliability, and recommendations for optimization. Return your analysis in JSON format with:
    {
      "summary": "executive summary of findings",
      "pricing_trends": "analysis of pricing patterns",
      "supplier_insights": "insights about each supplier",
      "recommendations": ["actionable recommendations"],
      "risk_assessment": "potential risks and mitigation strategies"
    }`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Analyze this supplier data: ${JSON.stringify(supplierData)}` },
      ],
      response_format: { type: 'json_object' },
      max_tokens: 2000,
      temperature: 0.3,
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error in supplier data analysis:', error);
    throw error;
  }
}

/**
 * Creates embeddings for product search and similarity matching.
 * @param {string} text - The input text to create embeddings for.
 * @returns {Promise<number[]>} The embedding vector.
 */
export async function createProductEmbedding(text) {
  try {
    const response = await openai?.embeddings?.create({
      model: 'text-embedding-3-small',
      input: text,
    });

    return response?.data?.[0]?.embedding;
  } catch (error) {
    console.error('Error creating product embedding:', error);
    throw error;
  }
}

/**
 * Moderates content for policy violations.
 * @param {string} text - The text to moderate.
 * @returns {Promise<object>} Moderation results.
 */
export async function moderateContent(text) {
  try {
    const response = await openai?.moderations?.create({
      model: 'text-moderation-latest',
      input: text,
    });

    return response?.results?.[0];
  } catch (error) {
    console.error('Error moderating content:', error);
    throw error;
  }
}

/**
 * Generates automated product descriptions based on product attributes.
 * @param {Object} productAttributes - Product attributes and specifications.
 * @returns {Promise<string>} Generated product description.
 */
export async function generateProductDescription(productAttributes) {
  const systemPrompt = `You are a professional product copywriter. Generate compelling, accurate product descriptions based on the provided attributes. Focus on key features, benefits, and technical specifications that would help in product matching and comparison.`;

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Generate a product description for: ${JSON.stringify(productAttributes)}` },
      ],
      max_tokens: 500,
      temperature: 0.6,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error generating product description:', error);
    throw error;
  }
}

/**
 * Performs intelligent data categorization and tagging.
 * @param {string} productData - Raw product data to categorize.
 * @returns {Promise<Object>} Categorization results with tags.
 */
export async function categorizeProduct(productData) {
  try {
    const systemPrompt = `You are a product categorization expert. Analyze the product data and provide structured categorization with tags, category hierarchy, and attributes. Return JSON format:
    {
      "primary_category": "main product category",
      "subcategory": "specific subcategory",
      "tags": ["relevant", "tags", "for", "search"],
      "attributes": {"key": "value pairs of important attributes"},
      "confidence": "confidence score 0-1"
    }`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Categorize this product: ${productData}` },
      ],
      response_format: { type: 'json_object' },
      max_tokens: 800,
      temperature: 0.2,
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error categorizing product:', error);
    throw error;
  }
}